/**
 * Cookie Consent Utility
 * 
 * Provides functions to check cookie consent status from anywhere in the app.
 * Works in conjunction with the CookieConsent component.
 */

export const COOKIE_CONSENT_KEY = "replenico_cookie_consent";
export const COOKIE_CONSENT_VERSION = "1.0";

export interface CookieConsentPreferences {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

/**
 * Get the stored cookie consent preferences
 * Returns null if no consent has been given or if version mismatch
 */
export function getStoredConsent(): CookieConsentPreferences | null {
  if (typeof window === "undefined") return null;
  
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      const consent = JSON.parse(stored) as CookieConsentPreferences;
      // Only return consent if version matches
      if (consent.version === COOKIE_CONSENT_VERSION) {
        return consent;
      }
    }
  } catch {
    // Invalid stored consent or localStorage not available
  }
  return null;
}

/**
 * Check if analytics cookies are allowed
 */
export function hasAnalyticsConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.analytics === true;
}

/**
 * Check if marketing cookies are allowed
 */
export function hasMarketingConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.marketing === true;
}

/**
 * Check if preference cookies are allowed
 */
export function hasPreferencesConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.preferences === true;
}

/**
 * Check if any consent has been given (user has interacted with cookie banner)
 */
export function hasConsentBeenGiven(): boolean {
  return getStoredConsent() !== null;
}

/**
 * Store cookie consent preferences
 */
export function setStoredConsent(consent: CookieConsentPreferences): void {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    
    // Dispatch custom event so analytics components can react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: consent 
    }));
  } catch {
    // Storage not available
  }
}

/**
 * Clear stored consent (for testing or user request)
 */
export function clearStoredConsent(): void {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: null 
    }));
  } catch {
    // Storage not available
  }
}
